import { IconCalendar, IconSitemap } from "@tabler/icons-react";
import { useMemo } from "react";
import type { CSSProperties } from "react";

import { ProjectLogo } from "@/components/common";
import { useBrandColorContext } from "@/contexts/BrandColorContext";
import { useT } from "@/i18n";
import { useTheme } from "@/ui";
import { cn } from "@/utils/cn";

export type AuthBannerProps = {
  className?: string;
};

export const AuthBanner = ({ className }: AuthBannerProps) => {
  const t = useT();
  const { title } = useBrandColorContext();
  const { theme, themeValues } = useTheme();
  const palette = themeValues.palette.brand;

  const loginBannerStyle = useMemo<CSSProperties>(() => {
    const topLeftColor = theme === "light" ? palette[400] : palette[600];
    const topRightColor = palette[500];
    const bottomLeftColor = theme === "light" ? palette[600] : palette[400];
    const bottomRightColor = theme === "light" ? palette[200] : palette[800];

    return {
      backgroundColor: palette[500],
      backgroundImage: [
        `radial-gradient(ellipse 75% 75% at 0% 0%, ${topLeftColor} 0%, transparent 100%)`,
        `radial-gradient(ellipse 75% 75% at 100% 0%, ${topRightColor} 0%, transparent 100%)`,
        `radial-gradient(ellipse 75% 75% at 0% 100%, ${bottomLeftColor} 0%, transparent 100%)`,
        `radial-gradient(ellipse 75% 75% at 100% 100%, ${bottomRightColor} 0%, transparent 100%)`,
      ].join(", "),
    };
  }, [palette, theme]);

  return (
    <section
      className={cn(
        "flex-1 h-full rounded-large md:max-w-[700px] p-4 py-6 md:p-6 lg:p-[50px] flex flex-col justify-center relative",
        className
      )}
      style={loginBannerStyle}
    >
      <div className="flex flex-col flex-1 justify-center">
        <header className="mb-6 sm:mb-9">
          <h2 className="text-light-primary font-grotesk text-2xl font-medium leading-[22px]">
            {t("auth.login.banner.title", { platformTitle: title })}
          </h2>
        </header>
        <div>
          <ul className="flex flex-col gap-6 md:gap-7">
            <LoginBannerItem
              icon={<IconSitemap size={25} />}
              title={t("auth.login.banner.platformsAndOrganizations.title")}
              description={t("auth.login.banner.platformsAndOrganizations.description")}
            />
            <LoginBannerItem
              icon={<IconCalendar size={25} />}
              title={t("auth.login.banner.toursAndCircuits.title")}
              description={t("auth.login.banner.toursAndCircuits.description")}
            />
          </ul>
        </div>
      </div>
      <footer className="mt-9" aria-hidden={true}>
        <ProjectLogo variant="light" width={147} height={24} />
      </footer>
    </section>
  );
};

type LoginBannerItemProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

export const LoginBannerItem = ({ icon, title, description }: LoginBannerItemProps) => {
  return (
    <li className="flex flex-col sm:flex-row gap-3 items-stretch">
      <div className="flex flex-col justify-center " aria-hidden={true}>
        <div className="bg-white/30 rounded-large text-white max-w-fit h-[60px] w-[60px] min-w-[60px] min-h-[60px] flex items-center justify-center">
          {icon}
        </div>
      </div>
      <div className="flex flex-col gap-3 justify-center">
        <h3 className="font-bold font-grotesk text-light-primary text-base leading-[17px]">
          {title}
        </h3>
        <p className="text-sm text-white/80 font-medium leading-[120%] whitespace-pre-wrap max-w-[450px]">
          {description}
        </p>
      </div>
    </li>
  );
};
